import { formatCurrency, formatDate } from '@eriko/shared-types';
import { Badge } from '../ui/badge';

interface InvoiceDetailLine {
  amount?: number;
  quantity?: number;
  description?: string;
}

/**
 * Renders the flat `entity-detail:Invoice` artifact emitted by the
 * create_invoice tool (customerName / totalAmount / lines[{amount,quantity,
 * description}]). The QBO-shaped click-to-view payload is handled separately
 * by InvoiceDetailView. Mirrors BillDetailReport.
 */
export function InvoiceDetailReport({ data }: { data: Record<string, unknown> }) {
  const customerName = data.customerName ? String(data.customerName) : '';
  const invoiceNumber = data.invoiceNumber ? String(data.invoiceNumber) : '';
  const date = data.date ? String(data.date) : '';
  const dueDate = data.dueDate ? String(data.dueDate) : '';
  const currencyName =
    (data.CurrencyRef as { name?: string } | undefined)?.name ?? 'United States Dollar';
  const totalAmount = Number(data.totalAmount ?? 0);
  const balance = Number(data.balance ?? totalAmount);
  const lines = (data.lines as InvoiceDetailLine[] | undefined) ?? [];

  const subtotal = lines.reduce((sum, l) => sum + Number(l.amount ?? 0), 0);

  const isPaid = balance === 0;
  const dueDateObj = dueDate ? new Date(dueDate) : null;
  const now = new Date();
  const isOverdue =
    !isPaid &&
    dueDateObj !== null &&
    !Number.isNaN(dueDateObj.getTime()) &&
    now > dueDateObj;
  const daysOverdue = isOverdue
    ? Math.floor((now.getTime() - (dueDateObj as Date).getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  return (
    <div className="space-y-0">
      {/* Header */}
      <div className="flex justify-between items-start mb-6 pb-5 border-b border-border/50">
        <div>
          <div
            className="text-[28px] text-foreground mb-2"
            style={{ fontFamily: '"Instrument Serif", Georgia, serif', fontWeight: 400 }}
          >
            Invoice
          </div>
          {isPaid && (
            <Badge className="bg-green-500/10 text-green-700 border-0">Paid</Badge>
          )}
        </div>
        <div className="text-left">
          <div className="space-y-0">
            {[
              ['Number', invoiceNumber || '—'],
              ['Issued', date ? formatDate(date) : '—'],
              ['Due', dueDate ? formatDate(dueDate) : '—'],
              ['Currency', currencyName],
            ].map(([label, value]) => (
              <div key={label} className="flex gap-2.5 py-[2px]">
                <span className="text-xs text-muted-foreground/70 w-[60px] flex-shrink-0">
                  {label}
                </span>
                <span className="font-mono text-xs font-medium text-foreground">
                  {value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Overdue badge */}
      {isOverdue && (
        <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-md mb-6 bg-amber-500/10 text-amber-700">
          <div className="w-[7px] h-[7px] rounded-full flex-shrink-0 bg-amber-600" />
          <span className="text-[13px] font-medium flex-1">
            {daysOverdue} day{daysOverdue !== 1 ? 's' : ''} overdue
          </span>
        </div>
      )}

      {/* Customer */}
      {customerName && (
        <div className="mb-5">
          <div className="text-[10px] font-semibold uppercase tracking-[0.8px] text-muted-foreground/70 mb-1.5">
            Bill To
          </div>
          <div className="text-sm font-semibold text-foreground">{customerName}</div>
        </div>
      )}

      {/* Line items */}
      {lines.length > 0 && (
        <table className="w-full border-collapse mb-0">
          <thead>
            <tr>
              <th className="text-left text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground/70 py-2 border-b border-border">
                Description
              </th>
              <th className="text-center text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground/70 py-2 border-b border-border w-[50px]">
                Qty
              </th>
              <th className="text-right text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground/70 py-2 border-b border-border w-[80px]">
                Rate
              </th>
              <th className="text-right text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground/70 py-2 border-b border-border w-[90px]">
                Amount
              </th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, i) => {
              const description = line.description ? String(line.description) : '—';
              const quantity = Number(line.quantity ?? 1);
              const amount = Number(line.amount ?? 0);
              const rate = quantity !== 0 ? amount / quantity : amount;
              const isLast = i === lines.length - 1;
              const borderClass = !isLast ? 'border-b border-border/50' : '';

              return (
                <tr key={i}>
                  <td className={`py-2.5 align-top ${borderClass}`}>
                    <div className="text-[13.5px] font-medium text-foreground">
                      {description}
                    </div>
                  </td>
                  <td
                    className={`py-2.5 text-center text-[13.5px] text-foreground/80 align-top ${borderClass}`}
                  >
                    {quantity}
                  </td>
                  <td
                    className={`py-2.5 text-right font-mono text-xs text-muted-foreground align-top ${borderClass}`}
                  >
                    {rate.toFixed(2)}
                  </td>
                  <td
                    className={`py-2.5 text-right font-mono text-[13px] font-medium text-foreground align-top ${borderClass}`}
                  >
                    {formatCurrency(amount, 2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Totals */}
      <div className="flex flex-col items-end my-4 mb-5">
        <div className="w-[220px]">
          <div className="flex justify-between items-center py-1.5 border-b border-border/50">
            <span className="text-[13px] text-muted-foreground">Subtotal</span>
            <span className="font-mono text-[13px] font-medium text-foreground">
              {formatCurrency(subtotal, 2)}
            </span>
          </div>
          <div className="flex justify-between items-center pt-2 mt-1 border-t border-border">
            <span className="text-xs font-medium text-foreground/80">
              {isPaid ? 'Total' : 'Balance Due'}
            </span>
            <span
              className={`font-mono text-[13px] font-medium px-1.75 py-0.5 rounded ${
                isPaid
                  ? 'bg-green-500/10 text-green-700'
                  : 'bg-red-500/10 text-red-600'
              }`}
            >
              {formatCurrency(isPaid ? totalAmount : balance, 2)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
